import { PublicClientApplication } from '@azure/msal-browser';
import { galRequest, graphConfig } from '../auth/msalConfig';

export interface GalUser {
  id: string;
  displayName: string;
  mail: string | null;
  userPrincipalName: string;
  jobTitle: string | null;
  department: string | null;
}

/** In-memory photo URL cache keyed by Graph user ID or email */
const photoCache = new Map<string, string | null>();

/** Acquire a Graph token (silent or popup) */
async function getGalToken(msalInstance: PublicClientApplication): Promise<string | null> {
  const accounts = msalInstance.getAllAccounts();
  if (accounts.length === 0) return null;
  try {
    const r = await msalInstance.acquireTokenSilent({ ...galRequest, account: accounts[0] });
    return r.accessToken;
  } catch {
    try {
      const r = await msalInstance.acquireTokenPopup(galRequest);
      return r.accessToken;
    } catch { return null; }
  }
}

/**
 * Fetch a user's profile photo from Graph as a blob URL.
 * Returns null if no photo or on error. Results are cached.
 */
export async function fetchUserPhoto(
  msalInstance: PublicClientApplication,
  userIdOrUpn: string,
): Promise<string | null> {
  if (photoCache.has(userIdOrUpn)) return photoCache.get(userIdOrUpn)!;

  const token = await getGalToken(msalInstance);
  if (!token) return null;

  try {
    const res = await fetch(`${graphConfig.usersEndpoint}/${userIdOrUpn}/photo/$value`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) { photoCache.set(userIdOrUpn, null); return null; }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    photoCache.set(userIdOrUpn, url);
    return url;
  } catch {
    photoCache.set(userIdOrUpn, null);
    return null;
  }
}

/**
 * Batch-fetch photos for multiple users. Returns a Map of email → blob URL.
 */
export async function fetchUserPhotos(
  msalInstance: PublicClientApplication,
  emailsOrIds: string[],
): Promise<Map<string, string | null>> {
  const result = new Map<string, string | null>();
  const toFetch = emailsOrIds.filter((id) => {
    if (photoCache.has(id)) { result.set(id, photoCache.get(id)!); return false; }
    return true;
  });
  await Promise.allSettled(
    toFetch.map(async (id) => {
      const url = await fetchUserPhoto(msalInstance, id);
      result.set(id, url);
    }),
  );
  return result;
}

/**
 * Fetch the signed-in user's own job title from Graph (/me?$select=jobTitle).
 * Uses a token already acquired with the User.Read scope (granted at login), so
 * no extra consent is needed. Returns null if absent or on error. Used to backfill
 * the user's Job Class on login when it is empty.
 */
export async function fetchMyJobTitle(accessToken: string): Promise<string | null> {
  try {
    const res = await fetch('https://graph.microsoft.com/v1.0/me?$select=jobTitle', {
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    if (!res.ok) return null;
    const data = await res.json();
    const jt = typeof data.jobTitle === 'string' ? data.jobTitle.trim() : '';
    return jt || null;
  } catch {
    return null;
  }
}

/**
 * Search the organization's Global Address List (GAL) via Microsoft Graph.
 * Requires User.ReadBasic.All scope — will prompt for consent if needed.
 */
export async function searchGal(
  msalInstance: PublicClientApplication,
  query: string,
): Promise<GalUser[]> {
  if (!query || query.length < 2) return [];

  const accessToken = await getGalToken(msalInstance);
  if (!accessToken) return [];

  // Use $search with ConsistencyLevel for better partial-match results
  const select = 'id,displayName,mail,userPrincipalName,jobTitle,department';
  const searchParam = encodeURIComponent(`"displayName:${query}" OR "mail:${query}"`);
  const url = `${graphConfig.usersEndpoint}?$search=${searchParam}&$select=${select}&$top=15&$filter=accountEnabled eq true`;

  try {
    const res = await fetch(url, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        ConsistencyLevel: 'eventual',
      },
    });

    if (!res.ok) {
      // Fallback to $filter if $search is not supported
      return searchGalFallback(accessToken, query);
    }

    const data = await res.json();
    return (data.value ?? []) as GalUser[];
  } catch {
    return searchGalFallback(accessToken, query);
  }
}

/** Fallback using $filter + startswith (less flexible but always works) */
async function searchGalFallback(accessToken: string, query: string): Promise<GalUser[]> {
  const q = query.replace(/'/g, "''");
  const filter = `startswith(displayName,'${q}') or startswith(mail,'${q}')`;
  const select = 'id,displayName,mail,userPrincipalName,jobTitle,department';
  const url = `${graphConfig.usersEndpoint}?$filter=${filter}&$select=${select}&$top=15`;

  try {
    const res = await fetch(url, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    if (!res.ok) return [];
    const data = await res.json();
    return (data.value ?? []) as GalUser[];
  } catch {
    return [];
  }
}
